import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Input, Select, Table, Tooltip } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { MapPin, MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { DateTime } from 'luxon';
import {
  IST_ZONE,
  PLACE_SORT_FIELDS,
  PLACE_SOURCES,
  type Paginated,
  type PlaceDto,
  type PlaceSortField,
} from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { useAuth } from '@/hooks/useAuth';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import { can, READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { MergePlaceModal } from './components/MergePlaceModal';
import { PlaceDialog } from './components/PlaceDialog';

/**
 * The place master — a GLOBAL gazetteer, shared by the whole commissionerate.
 *
 * ⚠️ There is no unit filter on this screen, and that is not an omission. A place
 * existing is not sensitive; a police arrangement at that place is, and that isolation
 * lives on the arrangement (FR-3.6). A gazetteer that fragments per division is one
 * nobody's autocomplete ever learns from, which is the whole premise of local-first
 * place lookup. The subtitle says so, on screen, for the same reason the service says it
 * in a comment.
 *
 * `useServerTable`'s fourth consumer, and again with no change to the hook.
 */
/**
 * [P4-UX] What the gazetteer is, and what it is not. See the empty state below.
 */
const PLACES_EMPTY_COPY = {
  description:
    'This is the shared master list every unit picks from, so a place added here can be reused, filtered and reported on. It is not a record of everywhere duty has been done: a location typed straight onto an arrangement stays on that order and does not appear here. Add the places your officers name most often.',
} as const;

const PlacesPage = () => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; place?: PlaceDto }>();
  const [merging, setMerging] = useState<PlaceDto | undefined>();

  const canWrite = officer ? can(officer.accessRole, 'place.write') : false;
  const canMerge = officer ? can(officer.accessRole, 'place.merge') : false;

  const table = useServerTable<PlaceDto, PlaceSortField>({
    // The SAME tuple the API feeds to `createPaginationSchema`. One allowlist.
    sortFields: PLACE_SORT_FIELDS,
    defaultSortBy: 'usageCount',
    defaultSortOrder: 'desc',
    filterKeys: ['source'],
    queryKey: (params) => queryKeys.places.list(params),
    queryFn: (params, signal) =>
      api.get<Paginated<PlaceDto>>('/places', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          ...params.filters,
        },
        signal,
      }),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.places.all });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.post<PlaceDto>(`/places/${id}/deactivate`),
    onSuccess: () => {
      void message.success('Place deactivated');
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const confirmDeactivate = (place: PlaceDto) =>
    modal.confirm({
      title: `Deactivate ${place.name}?`,
      content:
        'Deactivation is used instead of deletion, because a place may already be referenced. It stops appearing in search.',
      okText: 'Deactivate',
      okButtonProps: { danger: true },
      onOk: () => void deactivate.mutateAsync(place.id),
    });

  const rowActions = (place: PlaceDto): MenuProps['items'] => [
    {
      key: 'merge',
      label: 'Merge another place into this one…',
      disabled: !canMerge,
      onClick: () => setMerging(place),
    },
    {
      key: 'deactivate',
      label: 'Deactivate',
      danger: true,
      disabled: !canWrite || !place.isActive,
      onClick: () => void confirmDeactivate(place),
    },
  ];

  const allColumns: TableProps<PlaceDto>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      // [P2] The first column is the way in — see `NameCell`. The alternative names stay
      // OUTSIDE the control: they are what a merge preserves, so a place that has absorbed
      // others reads as one record with a history rather than a row that lost something —
      // but inside the control every row would be named "Esplanade also: Chowringhee".
      render: (name: string, row) => (
        <NameCell
          name={name}
          onOpen={() => setEditing({ mode: 'edit', place: row })}
          secondary={row.aliases.length > 0 ? `also: ${row.aliases.join(' · ')}` : undefined}
        />
      ),
    },
    {
      title: 'Address',
      key: 'address',
      render: (_value, row) => row.formattedAddress ?? <span className="text-subtle">—</span>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source: PlaceDto['source']) => (
        <span className="text-sm text-muted-foreground">{SOURCE_LABEL[source]}</span>
      ),
    },
    {
      title: 'Used',
      dataIndex: 'usageCount',
      key: 'usageCount',
      sorter: true,
      align: 'right',
      width: 90,
      render: (count: number) => (
        <span className={count === 0 ? 'text-muted-foreground tabular-nums' : 'tabular-nums'}>{count}</span>
      ),
    },
    {
      title: 'Last used',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      sorter: true,
      width: 140,
      render: (lastUsedAt: string | undefined) =>
        lastUsedAt ? (
          // luxon, always. There is no dayjs in this repository and ESLint blocks it.
          <span className="text-sm text-muted-foreground">
            {/* [P7C §6a] The IST day of the instant, never the browser's. */}
            {DateTime.fromISO(lastUsedAt, { zone: IST_ZONE }).toFormat('d LLL yyyy')}
          </span>
        ) : (
          <span className="text-subtle">—</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      // A DOT, not a filled badge. Colour means status and nothing else here, and a
      // column of filled pills is a wall of colour in which nothing stands out.
      render: (active: boolean) =>
        active ? <StatusDot tone="success" label="Active" /> : <StatusDot tone="neutral" label="Inactive" />,
    },
    {
      title: '',
      key: 'actions',
      // [P4-UX] Pinned: an action a scroll can hide is an action that is not there.
      fixed: 'right' as const,
      align: 'right',
      width: 120,
      render: (_value, row) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
            <Button
              type="text"
              size="small"
              disabled={!canWrite}
              onClick={() => setEditing({ mode: 'edit', place: row })}
            >
              Edit
            </Button>
          </Tooltip>
          <Dropdown menu={{ items: rowActions(row) }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" aria-label={`More actions for ${row.name}`}>
              <MoreHorizontal className="size-4" aria-hidden />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  /*
   * ⚠️ THE MENU GETS THE UNFILTERED ARRAY; THE TABLE GETS THE FILTERED ONE. That is the
   * circularity solved — by never letting the two be the same variable. Keys and locks are
   * DERIVED from the array above, so this page keeps no second list of columns.
   */
  const columnKeys = columnKeySet(allColumns ?? []);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'places' });
  const columns = (allColumns ?? []).filter((column) => columnVisibility.isVisible(String(column.key)));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={MapPin}
        title="Places"
        subtitle={`${table.total} across the Commissionerate — shared by every unit`}
        actions={
          <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
            <Button type="primary" disabled={!canWrite} onClick={() => setEditing({ mode: 'create' })}>
              New place
            </Button>
          </Tooltip>
        }
        filters={
          <>
            <FilterField label="Search" className="w-64">
              <Input.Search
                allowClear
                placeholder="Name or alias"
                value={table.searchInput}
                onChange={(event) => table.onSearchChange(event.target.value)}
                data-testid="place-search"
              />
            </FilterField>
            <FilterField label="Source" className="w-48">
              <Select
                allowClear
                placeholder="Any"
                className="w-full"
                value={table.params.filters.source || undefined}
                onChange={(value: string | undefined) => table.setFilter('source', value)}
                options={PLACE_SOURCES.map((source) => ({ value: source, label: SOURCE_LABEL[source] }))}
                data-testid="place-source-filter"
              />
            </FilterField>
            {/* The Columns menu sits IN the filter row, not in `actions`: it is a view control,
                the same argument `pages/Arrangements` makes for its Segmented. */}
            <ColumnsMenu
              className="ml-auto"
              columns={allColumns ?? []}
              visibility={columnVisibility}
              locked={columnKeys.locked}
            />
          </>
        }
      />

      <Table<PlaceDto>
        {...table.tableProps}
        columns={columns}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText:
            table.searchInput || Object.keys(table.params.filters).length > 0 ? (
              <EmptyState size="sm" title="No places match these filters." />
            ) : (
              /*
                ⚠️ [P4-UX] IT SAYS WHAT THIS LIST HOLDS AND WHAT IT DOES NOT — AND CHANGES
                NO BEHAVIOUR.

                The QA walkthrough read "0 across the Commissionerate / No places yet" while
                a PUBLISHED arrangement was using a place somebody had typed into it, and
                concluded the screen was broken. Confirmed against the live data: `places: 0`
                with one published arrangement.

                ⚠️ Whether a free-text location typed onto an arrangement should be PROMOTED
                to a Place record is an open product decision with real consequences — every
                typo would become one — so this pass deliberately does not decide it. The
                copy states the current behaviour as a fact and promises nothing, which is
                what leaves the decision open.
              */
              <EmptyState size="sm" title="No places yet." description={PLACES_EMPTY_COPY.description} />
            ),
        }}
      />

      {editing && (
        <PlaceDialog
          open
          mode={editing.mode}
          place={editing.place}
          readOnly={!canWrite}
          onClose={() => setEditing(undefined)}
          onSuccess={invalidate}
        />
      )}

      {merging && <MergePlaceModal place={merging} onClose={() => setMerging(undefined)} onSuccess={invalidate} />}
    </div>
  );
};

const SOURCE_LABEL: Record<PlaceDto['source'], string> = {
  MANUAL: 'Added by hand',
  EXTERNAL: 'Map provider',
  DERIVED: 'From duty markings',
};

export default PlacesPage;
